// «Группировка» в шапке «Планирования» (владелец 2026-10-17, эскиз claude.ai/artifact/9xM5C4NitCm5Ga1Ju2RXqm):
// конструктор до трёх уровней («Группировать по», «Затем по», «Затем по»), уровни меняются местами мышью
// за ⠿, своя группировка сохраняется под названием и включается тегом; «Без группировки» — всегда первым,
// не удаляется. Кнопка на панели узкая: название сохранённой группировки или «Группировка N».
// Сохранённые группировки лежат на сервере рядом с наборами фильтров (filter_presets, личные), с меткой
// PREFIKS в названии и data.vid = 'gruppirovka' — сервер и база не менялись; набор фильтров их не показывает.
import { useEffect, useRef, useState } from 'react';
import { call, p } from './api';
import type { ShellCtx } from './shell';

export const PREFIKS = 'гр⁞';
export type Razbivka = { id: number; name: string; osnovnaya: boolean };
type Preset = { id: number; name: string; data: { vid?: string; cep?: string[] } };

/** признаки уровня: ключ → подпись и множественное число (для «Уровня») */
export function priznaki(razbivki: Razbivka[]): [string, string, string][] {
  const osn = razbivki.find(r => r.osnovnaya)?.name || 'Этапы';
  return [
    ['etap', osn === 'Этапы' ? 'Этап' : osn, osn],
    ...razbivki.filter(r => !r.osnovnaya).map(r => ['rz:' + r.id, r.name, r.name] as [string, string, string]),
    ['stroka', 'Строка ГПР', 'Строки ГПР'],   // суммарная позиция графика (#120); работа без строки — сама себе строка
    ['rabota', 'Работа', 'Работы'], ['korpus', 'Корпус', 'Корпуса'],
    ['sistema', 'Группа работ', 'Группы'], ['mesto', 'Место', 'Места'], ['kontur', 'Контур', 'Контуры'],
    ['raskatka', 'Раскатка', 'Раскатки'], ['logika', 'Порядок этажей', 'Порядки'], ['kalendar', 'Календарь', 'Календари'],
    ['podr', 'Подрядчик', 'Подрядчики'],
  ];
}

export function Gruppirovka({ ctx, razbivki, cep, onCep }: {
  ctx: ShellCtx; razbivki: Razbivka[]; cep: string[]; onCep: (c: string[]) => void;
}) {
  const [otkr, setOtkr] = useState(false);
  const [presety, setPresety] = useState<Preset[]>([]);
  const [nazv, setNazv] = useState('');
  const [tyanu, setTyanu] = useState<{ ot: number; kuda: number } | null>(null);
  const okno = useRef<HTMLSpanElement>(null);
  const PR = priznaki(razbivki);
  const imya = (k: string) => PR.find(x => x[0] === k)?.[1] || k;
  const cepTekst = (c: string[]) => c.length ? c.map(imya).join(' › ') + ' › Задача' : 'Все задачи списком, без строк-итогов';

  async function zagruzit() {
    const r = await call<Preset[]>('get', p('/api/projects/{pid}/presets', { pid: ctx.pid }));
    if (r.ok) setPresety(r.data.filter(x => x.name.startsWith(PREFIKS) && Array.isArray(x.data?.cep)));
  }
  useEffect(() => { void zagruzit(); }, [ctx.pid]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {   // щелчок мимо окошка — закрыть
    if (!otkr) return;
    const f = (e: MouseEvent) => { if (okno.current && !okno.current.contains(e.target as Node) && (e.target as Node).isConnected) setOtkr(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOtkr(false); };   // Esc — закрыть (2026-10-17)
    document.addEventListener('mousedown', f);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', f); document.removeEventListener('keydown', esc); };
  }, [otkr]);

  const svoi = presety.map(x => ({ ...x, n: x.name.slice(PREFIKS.length) }));
  const tek = svoi.find(x => (x.data.cep || []).join('|') === cep.join('|'));
  const novoeImya = () => { let n = svoi.length + 1; while (svoi.some(x => x.n === 'Группировка ' + n)) n++; return 'Группировка ' + n; };
  // несохранённая цепочка — «Своя (не сохранена)», чтобы не путать с сохранённой (замер кликов, 2026-10-18)
  const knopka = !cep.length ? 'Без группировки' : tek ? tek.n : 'Своя (не сохранена)';

  async function sohranit() {
    if (!cep.length) return;
    const n = nazv.trim() || novoeImya();
    const r = await call('post', p('/api/projects/{pid}/presets', { pid: ctx.pid }), { name: PREFIKS + n, data: { vid: 'gruppirovka', cep } });
    if (!r.ok) { ctx.onError?.(r.error.message); return; }
    setNazv(''); await zagruzit();
  }
  async function udalit(id: number) {
    const r = await call('delete', '/api/presets/' + id);
    if (!r.ok) { ctx.onError?.(r.error.message); return; }
    await zagruzit();
  }
  /** ячейка уровня i: выбор признака; занятый в другой ячейке — недоступен */
  function smenit(i: number, v: string) {
    const c = cep.slice(); c[i] = v;
    const nov: string[] = [];
    for (const k of c) { if (!k) break; if (!nov.includes(k)) nov.push(k); }
    onCep(nov);
  }
  /** перетаскивание уровня за ⠿ */
  function tyanut(e: React.PointerEvent, ot: number) {
    e.preventDefault();
    let kuda = ot;
    setTyanu({ ot, kuda });
    document.body.classList.add('gr-tyanem');
    const dv = (ev: PointerEvent) => {
      const r = (document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null)?.closest<HTMLElement>('.gr-row');
      const i = r ? +(r.dataset.r || -1) : -1;
      kuda = i >= 0 && i < cep.length ? i : ot;
      setTyanu({ ot, kuda });
    };
    const up = () => {
      window.removeEventListener('pointermove', dv); window.removeEventListener('pointerup', up);
      document.body.classList.remove('gr-tyanem');
      setTyanu(null);
      if (kuda !== ot) { const c = cep.slice(); const [x] = c.splice(ot, 1); c.splice(kuda, 0, x); onCep(c); }
    };
    window.addEventListener('pointermove', dv); window.addEventListener('pointerup', up);
  }

  return (
    <span className="gr" ref={okno}>
      <span className="muted gr-lbl">Группировка</span>
      <button className="sec gr-btn" title={cepTekst(cep)} onClick={() => setOtkr(o => !o)}><span>{knopka}</span> ▾</button>
      {otkr && (
        <div className="gr-pop">
          <div className="gr-teg-ryad">
            <span className={'gr-teg' + (cep.length ? '' : ' on')} title="Все задачи списком, без строк-итогов" onClick={() => onCep([])}>Без группировки</span>
            {svoi.map(x => (
              <span key={x.id} className={'gr-teg' + (tek?.id === x.id ? ' on' : '')} title={cepTekst(x.data.cep || [])}
                onClick={() => onCep((x.data.cep || []).slice())}>{x.n}
                <button title="Удалить" onClick={e => { e.stopPropagation(); void udalit(x.id); }}>×</button></span>
            ))}
          </div>
          <div className="gr-urovni">
            {[0, 1, 2].map(i => (
              <div key={i} data-r={i} className={'gr-row' + (tyanu && tyanu.ot === i ? ' tyanu' : '') + (tyanu && tyanu.kuda === i && tyanu.ot !== i ? (i > tyanu.ot ? ' cel-n' : ' cel-v') : '')}>
                <span className="gr-ruch" title={cep[i] ? 'Потяните, чтобы поменять уровни местами' : undefined}
                  onPointerDown={cep[i] ? e => tyanut(e, i) : undefined}>{cep[i] ? '⠿' : ''}</span>
                <span>{i ? 'Затем по' : 'Группировать по'}</span>
                <select value={cep[i] || ''} onChange={e => smenit(i, e.target.value)}>
                  <option value="">—</option>
                  {PR.map(([k, t]) => <option key={k} value={k} disabled={cep.includes(k) && cep[i] !== k}>{t}</option>)}
                </select>
                {/* × — убрать уровень, нижние поднимаются (владелец 2026-10-18) */}
                {cep[i] ? <button type="button" className="gr-x" title="Убрать этот уровень"
                  onClick={() => onCep(cep.filter((_k, j) => j !== i))}>×</button> : <span />}
              </div>
            ))}
          </div>
          <div className="gr-niz">
            <span className="gr-sohr">
              <input value={nazv} placeholder={novoeImya()} maxLength={40} onChange={e => setNazv(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void sohranit(); }} />
              <button className="sec" disabled={!cep.length} onClick={() => void sohranit()}>Сохранить</button>
            </span>
            <a onClick={() => setOtkr(false)}>Готово</a>
          </div>
        </div>
      )}
    </span>
  );
}
